export interface KeyVals<T> {
  [key: string]: T;
}
export interface ObjectEntry<T> extends Array<string | T> {
  readonly 0: string;
  readonly 1: T;
  readonly length: 2;
}

export function normalizeURL(url: string): string {
  const parts = /(\w+:)(\/{2,})([^/]+)\/?([^#?]*)([^#]*)(.*)/.exec(url);
  if (parts == null) {
    throw TypeError('Invalid URL');
  }
  const [, protocol, slash, domain, path, query, hash] = parts;
  const altSlash = protocol in ['http:', 'https:'] || (protocol === 'file:' && slash !== '///')
                 ? '//'
                 : slash;
  const splittedPath: string[] = [];
  for (const val of path.split('/')) {
    switch (val) {
      case '.':
        continue;
      case '..':
        splittedPath.pop();
        break;
      default:
        splittedPath.push(val);
        break;
    }
  }
  return `${protocol}${altSlash}${domain}/${splittedPath.join('/')}${query}${hash}`;
}

function resolveURL_(relativePath: string, basePath: string): string {
  const regexp = /(\w[\w\d+\-\.]*:)(\/{2,})([^/]+)\/?([^#?]*)([^#]*)(.*)/;
  if (regexp.test(relativePath)) {
    return normalizeURL(relativePath);
  }
  const parts = regexp.exec(basePath);
  if (parts == null) {
    throw TypeError('Invalid URL');
  }
  const [, protocol, slash, domain, path, query] = parts;
  if (relativePath[0] === '?') {
    return normalizeURL(`${protocol}${slash}${domain}/${path}${relativePath}`);
  }
  if (relativePath[0] === '#') {
    return normalizeURL(`${protocol}${slash}${domain}/${path}${query}${relativePath}`);
  }
  if (relativePath.slice(0, 2) === '//') {
    return normalizeURL(`${protocol}${relativePath}`);
  }
  if (relativePath[0] === '/') {
    return normalizeURL(`${protocol}${slash}${domain}${relativePath}`);
  }
  if (path[path.length - 1] === '/') {
    return normalizeURL(`${protocol}${slash}${domain}/${path}${relativePath}`);
  }
  const index = Math.max(0, path.lastIndexOf('/'));
  return normalizeURL(`${protocol}${slash}${domain}/${path.slice(0, index)}/${relativePath}`);
}

export const resolveURL = typeof window.URL !== 'function'
                        ? resolveURL_
                        : (relativePath: string,
                           basePath: string) => new URL(relativePath, basePath).href;

export function* iterateEntries<T>(obj: Readonly<KeyVals<T>>): Iterator<ObjectEntry<T>> {
  for (const key of Object.keys(obj)) {
    yield [key, obj[key]];
  }
}

function entries_<T>(obj: Readonly<KeyVals<T>>) : ObjectEntry<T>[] {
  return Object.keys(obj).map((key:string): ObjectEntry<T> => [key, obj[key]]);
}

export function* iterateValues<T>(obj: Readonly<KeyVals<T>>) : Iterator<T> {
  for (const key of Object.keys(obj)) {
    yield obj[key];
  }
}

function values_<T>(obj: Readonly<KeyVals<T>>) : T[] {
  return Object.keys(obj).map((key:string): T => obj[key]);
}

interface ObjectConstructorNext extends ObjectConstructor {
  entries: typeof entries_;
  values: typeof values_;
}

export const entries = 'entries' in Object ? (Object as ObjectConstructorNext).entries : entries_;
export const values = 'values' in Object ? (Object as ObjectConstructorNext).values : values_;

export function* iterate<T>(array: ArrayLike<T>): Iterator<T> {
  for (let index = 0; index < array.length; index += 1) {
    yield array[index];
  }
}

export function assign<T>(target: T, ...sources: Readonly<T>[]): T;
export function assign<T>(target: KeyVals<T>, ...sources: Readonly<KeyVals<T>>[]): KeyVals<T>;

// Object.assignとは厳密には動作が異なる
export function assign<T>(target: KeyVals<T>, ...sources: Readonly<KeyVals<T>>[]) {
  for (const src of sources) {
    forEachIterator(iterateEntries(src), ([key, val]) => {
      target[key] = val;
    });
  }
  return target;
}

export function map<T, U>(array: ArrayLike<T>,
                          callback: (value: T, index: number, array: ArrayLike<T>) => U,
                          thisArg?: any): U[] {
  // XXX : Why this "as U[]" is needed?
  return Array.prototype.map.call(array, callback, thisArg) as U[];
}

export function forEach<T>(array: ArrayLike<T>,
                           callback: (value: T, index: number, array: ArrayLike<T>) => void,
                           thisArg?: any): void {
  Array.prototype.forEach.call(array, callback, thisArg);
}

export function filter<T>(array: ArrayLike<T>,
                          callback: (value: T, index: number, array: ArrayLike<T>) => boolean,
                          thisArg?: any): T[] {
　return Array.prototype.filter.call(array, callback, thisArg);
}

export function some<T>(array: ArrayLike<T>,
                        callback: (value: T, index: number, array: ArrayLike<T>) => boolean,
                        thisArg?: any): boolean {
  return Array.prototype.some.call(array, callback, thisArg);
}

export function forEachIterator<T, U>(iterator: Iterator<T>,
                                      callback: (value: T, index: number) => U,
                                      thisArg?: any): void;
export function forEachIterator<T, U>(iterator: Iterator<T>,
                                      callback: (value: T, index: number) => Promise<U>,
                                      thisArg?: any): Promise<U[]>;
export function forEachIterator<T, U>(iterator: Iterator<T>,
                                      callback: (value: T, index: number) => U | Promise<U>,
                                      thisArg?: any) {
  const call = thisArg !== undefined
               ? (value: T, index: number): U | Promise<U> => callback.call(thisArg, value, index)
               : callback;
  let index = 0;
  const promise: Promise<U>[] = [];
  for (let { value, done } = iterator.next(); !done; { value, done } = iterator.next()) {
    const r: U | Promise<U> = call(value, index);
    if (r instanceof Promise) {
      promise.push(r);
    }
    index += 1;
  }
  if (promise.length !== 0) {
    return Promise.all(promise);
  }
}

export function* mapIterator<T, U>(iterator: Iterator<T>,
                                   callback: (value: T, index: number) => Iterator<U>,
                                   thisArg?: any): Iterator<U> {
  let index = 0;
  for (let { value, done } = iterator.next(); !done; ({ value, done } = iterator.next())) {
    const iterator2: Iterator<U> = thisArg !== undefined
                      ? callback.call(thisArg, value, index)
                      : callback(value, index);
    for (let { value, done } = iterator2.next(); !done; ({ value, done } = iterator2.next())) {
      yield value;
    }
    index += 1;
  }
}

export function someIterator<T>(iterator: Iterator<T>,
                                callback: (value: T, index: number) => boolean,
                                thisArg?: any) {
  const call = thisArg !== undefined
               ? (value: T, index: number): boolean => callback.call(thisArg, value, index)
               : callback;
  let index = 0;
  for (let { value, done } = iterator.next(); !done; { value, done } = iterator.next()) {
    const r = call(value, index);
    if (r) {
      return true;
    }
    index += 1;
  }
  return false;
}

export function findIndex<T>(array: ArrayLike<T>,
                             callback: (value: T, index: number, array: ArrayLike<T>) => boolean,
                             thisArg?: any) {
  const call = thisArg !== undefined
               ? (value: T,
                  index: number,
                  array: ArrayLike<T>): boolean => callback.call(thisArg, value, index, array)
               : callback;
  let result = 0;
  while (result < array.length && !call(array[result], result, array)) {
    result += 1;
  }
  if (result === array.length) {
    return -1;
  }
  return result;
}

export function find<T>(array: ArrayLike<T>,
                        callback: (value: T, index: number, array: ArrayLike<T>) => boolean,
                        thisArg?: any) {
  const result = findIndex(array, callback, thisArg);
  if (result < 0) {
    return;
  }
  return array[result];
}

export function partitionIndex<T>(array: ArrayLike<T>,
                                  callback: (value: T,
                                             index: number,
                                             array: ArrayLike<T>) => boolean,
                                  thisArg?: any) {
  const call = thisArg !== undefined
               ? (value: T,
                  index: number,
                  array: ArrayLike<T>): boolean => callback.call(thisArg, value, index, array)
               : callback;
  let begin = 0;
  let end = array.length;
  while (begin !== end) {
    const i = Math.floor((begin + end) / 2);
    if (call(array[i], i, array)) {
      begin = i + 1;
    } else {
      end = i;
    }
  }
  return begin;
}

export function partition<T>(array: ArrayLike<T>,
                             callback: (value: T, index: number, array: ArrayLike<T>) => boolean,
                             thisArg?: any) {
  const result = partitionIndex(array, callback, thisArg);
  if (result >= array.length) {
    return;
  }
  return array[result];
}

export function clip(value: number,
                     base: number,
                     tail: number,
                     start: number,
                     end: number): number {
  return end < start ? start : Math.min(end, Math.max(start, value + (value < 0 ? tail : base)));
}
